import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Primary button variant from Figma (default)
        "primary-dark":
          "bg-gray-900 text-white font-montserrat font-bold text-[0.9375rem] rounded-lg hover:bg-gray-700 focus-visible:bg-gray-700 active:bg-gray-900 disabled:bg-gray-600 disabled:text-gray-400 transition-colors duration-200",
        // Primary red button variant from Figma (LoginForm)
        "primary-red":
          "bg-brand-red text-white font-montserrat font-bold text-base tracking-[0.3px] rounded-[0.375rem] hover:bg-red-700 focus-visible:bg-red-700 active:bg-red-900 disabled:bg-gray-600 disabled:text-gray-400 transition-colors duration-200",
        // Secondary button variant from Figma (Profile edit buttons)
        "secondary":
          "bg-white text-gray-500 border border-gray-500 rounded-[0.3125rem] font-montserrat font-bold text-xs uppercase hover:bg-gray-50 active:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 transition-colors duration-200",
        // Compact button variants from Figma (Trophy Room card actions)
        "compact-red":
          "bg-brand-red text-white font-montserrat font-bold text-xs uppercase rounded-[0.375rem] hover:bg-red-700 active:bg-red-900 disabled:bg-gray-600 disabled:text-gray-400 transition-colors",
        "compact-gray":
          "bg-gray-500 text-white font-montserrat font-bold text-xs uppercase rounded-[0.375rem] hover:bg-gray-600 active:bg-gray-700 disabled:bg-gray-300 disabled:text-gray-500 transition-colors",
        // Compact black button for product card buy actions (from Figma public product card)
        "compact-black":
          "bg-gray-900 text-white font-montserrat font-bold text-xs uppercase rounded-[0.375rem] hover:bg-gray-700 active:bg-gray-900 disabled:bg-gray-600 disabled:text-gray-400 transition-colors",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        // Custom sizes for Figma design
        "primary-dark": "h-auto px-6 py-3",
        "primary-red": "h-auto px-6 py-2.5",
        "compact": "h-auto p-2",
      },
    },
    defaultVariants: {
      variant: "primary-dark",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {leftIcon && (
          <span className="inline-flex items-center">{leftIcon}</span>
        )}
        {children}
        {rightIcon && (
          <span className="inline-flex items-center">{rightIcon}</span>
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
